import { CornerDownRight, Search, X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useSetTaskParent, useTaskSearch } from '@/hooks/useTasks';
import type { TaskDetail } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * File a task under another one, or set it free.
 *
 * The hierarchy used to be decided once, when the task was created: something
 * typed into Quick Add could never become a step of the thing it turned out to
 * belong to, and organising meant retyping it in the right place.
 *
 * Only tasks that may legally be a parent are offered — the server enforces
 * this anyway, but a list you cannot choose from is a worse way to learn a
 * rule than a list that never offers the impossible.
 */
export function ParentPicker({ task }: { task: TaskDetail }) {
  const t = useT();
  const [picking, setPicking] = useState(false);
  const [term, setTerm] = useState('');
  const setParent = useSetTaskParent(task.id);
  const { data: matches } = useTaskSearch(term);

  // A task that has steps of its own cannot become one: subtasks are one level
  // deep, and the two rules are the same rule read from either end.
  const canBecomeStep = task.subtask_total === 0;

  if (task.parent_task_id) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <CornerDownRight
          className="h-3.5 w-3.5 shrink-0 text-content-subtle"
          aria-hidden
        />
        <span className="min-w-0 flex-1 truncate text-content-muted">
          {t('task.stepOf', { title: task.parent_title ?? '' })}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setParent.mutate(null)}
          disabled={setParent.isPending}
        >
          {t('task.promote')}
        </Button>
      </div>
    );
  }

  if (!canBecomeStep) return null;

  if (!picking) {
    return (
      <Button
        variant="ghost"
        size="sm"
        className="-ml-2 gap-1.5"
        onClick={() => setPicking(true)}
      >
        <CornerDownRight className="h-3.5 w-3.5" aria-hidden />
        {t('task.makeStep')}
      </Button>
    );
  }

  const candidates = (matches?.items ?? []).filter(
    // Not itself, and not something that is already a step — the parent has to
    // be a task in its own right.
    (candidate) => candidate.id !== task.id && !candidate.parent_task_id,
  );

  return (
    // Its own card. On the detail pane the plain input sat on a background a
    // shade away from its own and read as a hole rather than a field; a panel
    // with an edge and a shadow says "this is a thing you are doing now".
    <div className="space-y-2 rounded-xl border border-border bg-surface p-3 shadow-xs">
      <p className="text-xs font-medium uppercase tracking-wide text-content-subtle">
        {t('task.makeStep')}
      </p>
      <div className="flex items-center gap-2">
        <div className="relative min-w-0 flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-subtle"
            aria-hidden
          />
          <Input
            autoFocus
            value={term}
            onChange={(event) => setTerm(event.target.value)}
            placeholder={t('task.makeStep.search')}
            className="bg-surface-sunken pl-8"
          />
        </div>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('common.close')}
          onClick={() => {
            setPicking(false);
            setTerm('');
          }}
        >
          <X className="h-4 w-4" aria-hidden />
        </Button>
      </div>

      {term.trim().length >= 2 ? (
        <ul className="max-h-52 divide-y divide-border overflow-y-auto rounded-lg border border-border bg-surface-sunken">
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <button
                type="button"
                onClick={() => {
                  setParent.mutate(candidate.id);
                  setPicking(false);
                  setTerm('');
                }}
                className={cn(
                  'w-full truncate px-3 py-2 text-left text-sm text-content',
                  'transition-surface hover:bg-surface-hover',
                )}
              >
                {candidate.title}
              </button>
            </li>
          ))}
          {candidates.length === 0 ? (
            <li className="px-3 py-2 text-sm text-content-subtle">
              {t('common.noResults')}
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}
